import { createAuthAxios, handleApiError } from "../utils/apiUtils";
import { API_BASE_URL } from "../config/apiConfig";

/**
 * Helper function to validate IDs
 * @param {string} id - ID to validate
 * @returns {boolean} Whether the ID is valid
 */
const isValidId = (id) => {
  return typeof id === "string" && id.trim().length > 0;
};

/**
 * Service for research feed-related API operations
 */
const feedService = {
  /**
   * Get feed posts
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Number of posts per page (default: 10)
   * @param {string} params.filter - Filter by post type ('research', 'questions', 'photos')
   * @returns {Promise<Object>} Feed posts with pagination data
   */
  getFeedPosts: async (params = {}) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get("/posts/feed", { params });
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Get post by ID
   * @param {string} postId - Post ID
   * @returns {Promise<Object>} Post data
   */
  getPostById: async (postId) => {
    try {
      if (!isValidId(postId)) {
        throw new Error("Invalid post ID");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(`/posts/${postId}`);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Get pre-signed URL for photo upload
   * @param {Object} fileData - File data
   * @param {string} fileData.filename - File name
   * @param {string} fileData.contentType - File content type
   * @returns {Promise<Object>} Pre-signed URL data
   */
  getPhotoUploadUrl: async (fileData) => {
    try {
      if (!fileData || !fileData.filename || !fileData.contentType) {
        throw new Error("Invalid file data");
      }

      // Validate content type for security
      const validImageTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!validImageTypes.includes(fileData.contentType)) {
        throw new Error("Invalid image file type");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post("/posts/upload-photo", fileData);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Get pre-signed URL for paper PDF upload
   * @param {Object} fileData - File data
   * @param {string} fileData.filename - File name
   * @param {string} fileData.contentType - File content type
   * @returns {Promise<Object>} Pre-signed URL data
   */
  getPaperUploadUrl: async (fileData) => {
    try {
      if (!fileData || !fileData.filename || !fileData.contentType) {
        throw new Error("Invalid file data");
      }

      // Validate content type for security
      if (fileData.contentType !== "application/pdf") {
        throw new Error("Invalid file type. Only PDF is allowed.");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        "/posts/upload-paper-pdf",
        fileData
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Upload file to pre-signed URL
   * @param {string} uploadUrl - Pre-signed URL
   * @param {File} file - File to upload
   * @param {string} contentType - File content type
   * @returns {Promise<void>}
   */
  uploadFileToS3: async (uploadUrl, file, contentType) => {
    try {
      if (!uploadUrl || !file || !contentType) {
        throw new Error("Missing required parameters for file upload");
      }

      // Validate URL is from trusted domain
      const urlObj = new URL(uploadUrl);
      if (!urlObj.hostname.endsWith("amazonaws.com")) {
        throw new Error("Invalid upload URL");
      }

      await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": contentType,
        },
      });
      return true;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create a new post
   * @param {Object} postData - Post data
   * @returns {Promise<Object>} Created post
   */
  createPost: async (postData) => {
    try {
      if (!postData || typeof postData !== "object") {
        throw new Error("Invalid post data");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post("/posts", postData);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Update a post
   * @param {string} postId - Post ID
   * @param {Object} postData - Updated post data
   * @returns {Promise<Object>} Updated post
   */
  updatePost: async (postId, postData) => {
    try {
      if (!isValidId(postId) || !postData || typeof postData !== "object") {
        throw new Error("Invalid post ID or data");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.put(`/posts/${postId}`, postData);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Delete a post
   * @param {string} postId - Post ID
   * @param {Object} params - Query parameters
   * @param {boolean} params.permanent - Whether to permanently delete (default: false)
   * @returns {Promise<Object>} Response data
   */
  deletePost: async (postId, params = {}) => {
    try {
      if (!isValidId(postId)) {
        throw new Error("Invalid post ID");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.delete(`/posts/${postId}`, { params });
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Like or unlike a post
   * @param {string} postId - Post ID
   * @returns {Promise<Object>} Updated like status
   */
  toggleLike: async (postId) => {
    try {
      if (!isValidId(postId)) {
        throw new Error("Invalid post ID");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(`/posts/${postId}/like`);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Add a comment to a post
   * @param {string} postId - Post ID
   * @param {Object} commentData - Comment data
   * @param {string} commentData.content - Comment content
   * @returns {Promise<Object>} Created comment
   */
  addComment: async (postId, commentData) => {
    try {
      if (
        !isValidId(postId) ||
        !commentData ||
        !commentData.content ||
        typeof commentData.content !== "string"
      ) {
        throw new Error("Invalid post ID or comment data");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `/posts/${postId}/comments`,
        commentData
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Like or unlike a comment
   * @param {string} postId - Post ID
   * @param {string} commentId - Comment ID
   * @returns {Promise<Object>} Updated like status
   */
  toggleCommentLike: async (postId, commentId) => {
    try {
      if (!isValidId(postId) || !isValidId(commentId)) {
        throw new Error("Invalid post ID or comment ID");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `/posts/${postId}/comments/${commentId}/like`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Delete a comment
   * @param {string} postId - Post ID
   * @param {string} commentId - Comment ID
   * @returns {Promise<Object>} Response data
   */
  deleteComment: async (postId, commentId) => {
    try {
      if (!isValidId(postId) || !isValidId(commentId)) {
        throw new Error("Invalid post ID or comment ID");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        `/posts/${postId}/comments/${commentId}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Get suggested researchers
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of researchers to return (default: 5)
   * @returns {Promise<Object>} Suggested researchers data
   */
  getSuggestedResearchers: async (params = {}) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get("/posts/suggested-researchers", {
        params,
      });
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Save or unsave a post (bookmark)
   * @param {string} postId - Post ID
   * @returns {Promise<Object>} Updated save status
   */
  savePost: async (postId) => {
    try {
      if (!isValidId(postId)) {
        throw new Error("Invalid post ID");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(`/posts/${postId}/save`);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Toggle like on a comment
   * @param {string} postId - Post ID
   * @param {string} commentId - Comment ID
   * @returns {Promise<Object>} Updated like status
   */
  toggleCommentLike: async (postId, commentId) => {
    try {
      if (!isValidId(postId) || !isValidId(commentId)) {
        throw new Error("Invalid post ID or comment ID");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `/posts/${postId}/comments/${commentId}/like`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Add a reply to a comment
   * @param {string} postId - Post ID
   * @param {string} commentId - Comment ID
   * @param {string} content - Reply content
   * @returns {Promise<Object>} Created reply
   */
  addCommentReply: async (postId, commentId, content) => {
    try {
      if (
        !isValidId(postId) ||
        !isValidId(commentId) ||
        typeof content !== "string" ||
        !content.trim()
      ) {
        throw new Error("Invalid post ID, comment ID, or reply content");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `/posts/${postId}/comments/${commentId}/replies`,
        { content }
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Delete a reply from a comment
   * @param {string} postId - Post ID
   * @param {string} commentId - Comment ID
   * @param {string} replyId - Reply ID
   * @returns {Promise<Object>} Response data
   */
  deleteCommentReply: async (postId, commentId, replyId) => {
    try {
      if (!isValidId(postId) || !isValidId(commentId) || !isValidId(replyId)) {
        throw new Error("Invalid post ID, comment ID, or reply ID");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        `/posts/${postId}/comments/${commentId}/replies/${replyId}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Repost a post
   * @param {string} postId - Post ID to repost
   * @param {string} caption - Optional caption for the repost
   * @returns {Promise<Object>} Created repost
   */
  repostPost: async (postId, caption = "") => {
    try {
      if (!isValidId(postId)) {
        throw new Error("Invalid post ID");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(`/posts/${postId}/repost`, {
        content: caption,
      });
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },
};

export default feedService;
